// ---------------------------------------------------------------------------
// Suppliers
// ---------------------------------------------------------------------------

/** The longest credit period a supplier's default terms may carry, in days. */
export const MAX_SUPPLIER_PAYMENT_TERMS_DAYS = 365;

/** A company the tenant buys from, as a list shows it. Tenant-wide: every branch buys from the same suppliers. */
export interface SupplierSummary {
  id: string;
  /** SUP-000001. The one identifier that is unique; names may repeat. */
  supplierNumber: string;
  name: string;
  nameAr: string | null;
  phone: string | null;
  paymentTermsDays: number;
  isActive: boolean;
  /** Moves by one on every change. Edits and (de)activation send back the version the screen showed. */
  version: number;
  createdAt: string;
  updatedAt: string;
}

/** Everything recorded about a supplier. */
export interface SupplierDetail extends SupplierSummary {
  contactPerson: string | null;
  email: string | null;
  taxNumber: string | null;
  address: string | null;
  notes: string | null;
}

/** Which filter a supplier list applies to the active flag. */
export const SupplierActiveFilter = {
  ACTIVE: 'true',
  INACTIVE: 'false',
  ALL: 'all',
} as const;
export type SupplierActiveFilter = (typeof SupplierActiveFilter)[keyof typeof SupplierActiveFilter];

/** Why an existing supplier looks like the one being entered, strongest signal first. */
export const SupplierDuplicateReason = {
  TAX_NUMBER: 'TAX_NUMBER',
  PHONE: 'PHONE',
  NAME: 'NAME',
} as const;
export type SupplierDuplicateReason =
  (typeof SupplierDuplicateReason)[keyof typeof SupplierDuplicateReason];

/** An existing supplier that may be the same company. Only identifying fields: never contact details. */
export interface SupplierDuplicateMatch {
  id: string;
  supplierNumber: string;
  name: string;
  nameAr: string | null;
  isActive: boolean;
  reason: SupplierDuplicateReason;
}

/** A warning, never a refusal: two real suppliers can share a name, a phone or a tax number. */
export interface SupplierDuplicateCheckResult {
  hasPossibleDuplicates: boolean;
  matches: SupplierDuplicateMatch[];
}
